import { cva, type VariantProps } from "class-variance-authority";
import type { ComponentPropsWithoutRef, ElementType } from "react";
import { cn } from "@/shared/lib/cn";

/**
 * A themed surface — the building block for panels, cards, the sidebar, the
 * composer shell, dropdowns. Polymorphic via `as` so semantics stay correct
 * (`as="aside"` for the sidebar, `as="section"` for a region, …).
 *
 * Encapsulates the tone/border/radius/elevation tokens so feature code never
 * writes `bg-paper border-line-strong` by hand.
 */
export const surfaceVariants = cva("", {
  variants: {
    tone: {
      canvas: "bg-canvas text-ink",
      soft: "bg-canvas-soft text-ink",
      paper: "bg-paper text-ink",
      "panel-2": "bg-panel-2 text-ink",
      sidebar: "bg-sidebar text-ink",
      glass: "bg-glass text-ink",
    },
    border: {
      none: "",
      hairline: "border border-line",
      strong: "border border-line-strong",
    },
    radius: {
      none: "",
      sm: "rounded-sm",
      md: "rounded-md",
      lg: "rounded-lg",
      pill: "rounded-pill",
    },
    shadow: {
      none: "",
      soft: "shadow-[var(--shadow-soft)]",
      pill: "shadow-[var(--shadow-pill)]",
    },
  },
  defaultVariants: { tone: "paper", border: "none", radius: "none", shadow: "none" },
});

type SurfaceProps<E extends ElementType> = {
  as?: E;
} & VariantProps<typeof surfaceVariants> &
  Omit<ComponentPropsWithoutRef<E>, "as" | keyof VariantProps<typeof surfaceVariants>>;

export function Surface<E extends ElementType = "div">({
  as,
  className,
  tone,
  border,
  radius,
  shadow,
  ...props
}: SurfaceProps<E>) {
  const Comp = (as ?? "div") as ElementType;
  return (
    <Comp className={cn(surfaceVariants({ tone, border, radius, shadow }), className)} {...props} />
  );
}
